import * as React from 'react';
import Badge from '@mui/material/Badge';
import {
  useGridSelector,
  useGridApiContext,
  useGridRootProps,
  gridPreferencePanelStateSelector,
  GridPreferencePanelsValue,
} from '@mui/x-data-grid';

/**
 * Column header filter button that is always visible (not only when column has active filter).
 * Renders to the left of the column menu (three dots). Click opens the default filter panel for this column.
 * Shows a badge when the column has active filters.
 */
function ColumnHeaderFilterButton(props) {
  const { counter, field, onClick } = props;
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const preferencePanel = useGridSelector(apiRef, gridPreferencePanelStateSelector);
  const labelId = React.useId();
  const panelId = React.useId();

  const colDef = apiRef.current?.getColumn?.(field);
  const toggleFilter = React.useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      const { open, openedPanelValue } = gridPreferencePanelStateSelector(apiRef.current.state);
      if (open && openedPanelValue === GridPreferencePanelsValue.filters) {
        apiRef.current.hideFilterPanel();
      } else {
        apiRef.current.showFilterPanel(field, panelId, labelId);
      }
      if (onClick) {
        onClick(apiRef.current.getColumnHeaderParams(field), event);
      }
    },
    [apiRef, field, onClick, panelId, labelId]
  );

  if (!colDef || rootProps.disableColumnFilter || colDef.filterable === false) {
    return null;
  }

  const open = preferencePanel?.open && preferencePanel?.labelId === labelId;
  const BaseIconButton = rootProps.slots.baseIconButton;
  const ColumnFilteredIcon = rootProps.slots.columnFilteredIcon;
  const BaseTooltip = rootProps.slots.baseTooltip;

  const iconButton = (
    <BaseIconButton
      {...(rootProps.slotProps?.baseIconButton || {})}
      id={labelId}
      onClick={toggleFilter}
      color="default"
      aria-label={apiRef.current.getLocaleText('columnHeaderFiltersLabel')}
      size="small"
      tabIndex={-1}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? panelId : undefined}
    >
      <ColumnFilteredIcon fontSize="small" />
    </BaseIconButton>
  );

  const wrapped =
    counter != null && counter > 1 ? (
      <Badge badgeContent={counter} color="default">
        {iconButton}
      </Badge>
    ) : (
      iconButton
    );

  const tooltipTitle =
    counter != null && counter > 0
      ? apiRef.current.getLocaleText('columnHeaderFiltersTooltipActive')(counter)
      : apiRef.current.getLocaleText('columnHeaderFiltersLabel');

  return (
    <BaseTooltip
      {...(rootProps.slotProps?.baseTooltip || {})}
      title={tooltipTitle}
      enterDelay={1000}
    >
      <span style={{ display: 'flex' }}>{wrapped}</span>
    </BaseTooltip>
  );
}

export default ColumnHeaderFilterButton;
